import { redirect } from '@tanstack/react-router';
import type { MeResponse } from '@wit-support/admin-contracts';
import type { AdminClientModule, AdminWorkspaceId } from '@wit-support/ui-domain';
import { landingPath, navModulesFor } from './workspace-nav.js';

export interface ModuleAccessGuardInput {
  /**
   * Resolves the current identity. Injected rather than reached for, so the
   * guard has no opinion on WHERE `me` comes from — the shell hands it the
   * shared `['me']` query, a test hands it a stub. Resolves to `null` on a
   * genuine 401 and REJECTS only on transient failures (5xx / network); the
   * two outcomes are treated differently below.
   */
  loadMe: () => Promise<MeResponse | null>;
  /** The module's declared nav permission (`AdminClientModule.permission`). */
  permission: string;
  /** Base path of the module being entered, e.g. `/pipelines`. */
  basePath: string;
  /** Where a refused navigation lands for the support ladder — the inbox. */
  fallbackPath: string;
  /**
   * Permission of {@link fallbackPath}'s module (`conversations.list`). An
   * operator WITHOUT it — a scoped role such as `marketing` («Реклама» only) —
   * is sent to {@link landing} instead: bouncing it to an inbox it cannot open
   * would render a page of 403s (or loop).
   */
  fallbackPermission?: string;
  /**
   * Landing path for an operator who cannot open the fallback module — usually
   * the first module of its first available workspace. `null` → `/` (the
   * Russian «нет доступных разделов» placeholder).
   */
  landing?: (me: MeResponse) => string | null;
}

/**
 * Route guard for one module's routes.
 *
 * Hiding the sidebar entry was the ONLY client-side check: typing `/pipelines`
 * rendered the page in full for any authenticated operator, and the refusal
 * arrived later as a scattering of 403s from the API. No data leaked (every GET
 * is policy-gated server-side), but the panel behaved unpredictably for
 * restricted roles — and that was the shared root cause behind the missing
 * per-action gates in templates / payment / admins / merge.
 *
 * This is DEFENCE IN DEPTH, never the enforcement point: the server policies
 * stay exactly as strict as they were. Hence the two deliberate fail-OPEN
 * branches — an unknown identity lets the route render and leaves the decision
 * to `MeGate`'s retry screen (transient) or its login screen (401), rather than
 * bouncing an operator out of the page they asked for because of one flaky
 * request.
 */
export async function moduleAccessGuard({
  loadMe,
  permission,
  basePath,
  fallbackPath,
  fallbackPermission,
  landing,
}: ModuleAccessGuardInput): Promise<void> {
  let me: MeResponse | null;
  try {
    me = await loadMe();
  } catch {
    return; // transient — MeGate renders its retry affordance
  }
  if (!me) return; // genuine 401 — MeGate renders the login screen

  if (me.permissions.includes(permission)) return;

  // Support ladder → the inbox (every support role holds it). Anyone who cannot
  // open the inbox either → its own landing module, or `/` when it has none.
  const canOpenFallback =
    fallbackPermission === undefined || me.permissions.includes(fallbackPermission);
  const target = canOpenFallback ? fallbackPath : ((landing ? landing(me) : null) ?? '/');

  // Never redirect a module onto itself: a role somehow missing the fallback's
  // permission would otherwise ping-pong forever.
  if (target === basePath) return;

  // `href`, not `to`: the route tree is assembled at runtime from the module
  // registry, so TanStack's generated path union contains only '/' and a
  // module path is not expressible in it. A relative href stays a
  // client-side navigation (a full-document reload is inferred only for an
  // ABSOLUTE href) — no cast, no page reload.
  throw redirect({ href: target });
}

export interface LandingRedirectInput {
  /** Как у {@link moduleAccessGuard}: `null` — 401, исключение — временный сбой. */
  loadMe: () => Promise<MeResponse | null>;
  /** Полный реестр модулей шелла. */
  modules: readonly AdminClientModule[];
  /** Сохранённый выбор пространства («куда приземлиться на `/`»). */
  readStored: () => AdminWorkspaceId | null;
}

/**
 * Охрана корня `/`: ведёт в первый модуль сохранённого или первого доступного
 * пространства — отдельной главной страницы у админки нет, а английская
 * заглушка «Welcome» никому не была нужна. Без доступных модулей, при
 * временном сбое или без сессии ничего не делает: корень рендерит русскую
 * заглушку, а сбой и вход решает `MeGate`.
 */
export async function landingRedirect({
  loadMe,
  modules,
  readStored,
}: LandingRedirectInput): Promise<void> {
  let me: MeResponse | null;
  try {
    me = await loadMe();
  } catch {
    return;
  }
  if (!me) return;
  const target = landingPath(navModulesFor(modules, me.permissions), readStored());
  // `href`, а не `to` — по той же причине, что и выше: модульные пути не входят
  // в сгенерированный союз путей роутера.
  if (target) throw redirect({ href: target });
}
